import type { ListingCardPriceInformation } from '@ha/core/Listing/getListingCardPriceInformation';

import { ListingCardModel } from 'ha/models/Listing/types';
import { centsToMajor } from 'ha/models/Listing/utilities/centsToMajor';

import { getRentReductionDisplay } from 'ha/modules/Listing/utilities/getRentReductionDisplay';

/**
 * Card-side adapter for rent-reduction strikethrough. Applies listing-card
 * gates (unit-type only, single flat value, not a "from" price), maps the
 * major-unit card price / unix `untilTS` into the shared
 * `getRentReductionDisplay` input (cents / ISO), and returns major units.
 */
export function getListingCardRentReduction({
  listing,
  priceInformation,
  now = Date.now(),
}: {
  listing: ListingCardModel;
  priceInformation: ListingCardPriceInformation | null;
  now?: number;
}): { previousPrice: number } | null {
  if (listing.identifiers.recordType === 'property') {
    return null;
  }

  if (!priceInformation || priceInformation.type !== 'value') {
    return null;
  }

  if (priceInformation.isMinimum) {
    return null;
  }

  const rentReduction =
    listing.source === 'algolia' ? listing.pricing.rentReduction : undefined;
  const rentReducedUntilTS = rentReduction?.untilTS;
  const rentReducedUntil =
    rentReducedUntilTS != null && rentReducedUntilTS > 0
      ? new Date(rentReducedUntilTS * 1000).toISOString()
      : null;

  const reduction = getRentReductionDisplay({
    pricingType: listing.pricing.priceType,
    currentPriceCents: priceInformation.value * 100,
    oldPriceCents: rentReduction?.oldPriceCents ?? null,
    rentReducedUntil,
    now,
  });

  if (!reduction) {
    return null;
  }

  return { previousPrice: centsToMajor(reduction.oldPriceCents) };
}
